import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  deactivateProject,
  exportToExcel,
  getActiveProjectsData,
  getActiveProjectsUtilization,
  getDomainAdminGridConfig,
  getDomainAdminGridData,
  getEndToEnd,
  getReviewDashboardData,
  getReviewerProductivityData,
  getReviewGridData,
  getReviewManagerList,
  getReviewProgress,
  getReviewRateList,
  getTagClassificationDetails,
  getUsersInActiveProjects,
  getWidgetsList,
  saveColumnDetails,
  saveWidgetData,
  saveWidgetSequence,
  getTagTreeData,
  getReviewerList,
  setRoleToRMU,
  getTaggingWidgetData,
  getTagClassifications,
  saveReviewerProductivityData,
  saveReviewProgress,
  getRedactionTag,
  deleteWidget,
  drawAssignmentFromPool,
  getAllProjectsData,
  getMenusData,
} from '../../api/endpoints/dashboardApi';
import {
  ActiveProjectsData,
  ActiveProjectUtilizationData,
  DomainAdminDasahboardGridConfig,
  DomainAdminGridData,
  EndToEndData,
  GetMenusType,
  IDashBoardHeaderResponse,
  RedactionTag,
  ReviewerDashboard,
  ReviewerProductivity,
  ReviewerProductivityWidget,
  ReviewerProgressData,
  ReviewerProgressFilterDataRPW,
  ReviewProductivityRequest,
  ReviewRate,
  ReviewTagCount,
  TagClassificationItem,
  TaggingWidgetData,
  UsersInActiveProjectData,
  WidgetItem,
} from '../../components/dashboard/dashboardTypes';
import {AuthenticatedUser} from '../../redux/slice/authenticatedUserSlice';

export const useReviewDashboardData = () => {
  return useQuery<ReviewerDashboard, Error>({
    queryKey: ['reviewDashboard'],
    queryFn: () => getReviewDashboardData(),
    placeholderData: undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    networkMode: 'always',
    gcTime: 0,
  });
};
export const useReviewProgress = (payload: ReviewerProgressFilterDataRPW) => {
  return useQuery<ReviewerProgressData, Error>({
    queryKey: ['ReviewerProgress', payload],
    queryFn: () => getReviewProgress(payload),
  });
};

export const useReviewRate = (isDefault: boolean) => {
  return useQuery<ReviewRate[], Error>({
    queryKey: ['ReviewRate'],
    queryFn: () => getReviewRateList(isDefault),
  });
};

export const useTaggingWidget = (payload: ReviewTagCount) => {
  return useQuery<TaggingWidgetData, Error>({
    queryKey: ['TaggingWidgetData', payload],
    queryFn: () => getTaggingWidgetData(payload),
  });
};

export const useReviewerProductivity = (payload: ReviewerProductivity) => {
  return useQuery<ReviewerProductivityWidget, Error>({
    queryKey: ['getReviewerProductivityData', payload],
    queryFn: () => getReviewerProductivityData(payload),
  });
};

export const useSaveWidgetData = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: saveWidgetData,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveWidgetSequence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: saveWidgetSequence,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAssignmentGridData = () => {
  return useQuery({
    queryKey: ['useAssignmentGridData'],
    queryFn: () => getReviewGridData(),
  });
};

export const useWidgetList = () => {
  return useQuery<WidgetItem[], Error>({
    queryKey: ['useWidgetList'],
    queryFn: () => getWidgetsList(),
  });
};

export const useDeleteWidget = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: deleteWidget,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useTagClassificationDetails = () => {
  return useQuery({
    queryKey: ['tagClassification'],
    queryFn: () => getTagClassificationDetails(),
  });
};

export const useEndToEnd = () => {
  return useQuery<EndToEndData, Error>({
    queryKey: ['EndToEndData'],
    queryFn: () => getEndToEnd(),
  });
};

export const useReviewRateList = () => {
  return useQuery<ReviewRate[], Error>({
    queryKey: ['reviewRate'],
    queryFn: () => getReviewManagerList(),
  });
};

export const useTagTreeData = () => {
  return useQuery({
    queryKey: ['tagTreeData'],
    queryFn: () => getTagTreeData(),
    enabled: false,
  });
};

export const useReviewerList = () => {
  return useQuery({
    queryKey: ['reviewerList'],
    queryFn: () => getReviewerList(),
    enabled: false,
  });
};

export const useActiveProjectData = (entityId: number) => {
  return useQuery<ActiveProjectsData, Error>({
    queryKey: ['activeProject', entityId],
    queryFn: () => getActiveProjectsData(entityId),
  });
};

export const useActiveProjectUtilization = (entityId: number) => {
  return useQuery<ActiveProjectUtilizationData, Error>({
    queryKey: ['getActiveProjectsUtilization', entityId],
    queryFn: () => getActiveProjectsUtilization(entityId),
  });
};

export const useUsersInActiveProjectData = (entityId: number) => {
  return useQuery<UsersInActiveProjectData, Error>({
    queryKey: ['getUsersInActiveProjects', entityId],
    queryFn: () => getUsersInActiveProjects(entityId),
  });
};

export const useDomainAdminGridConfig = () => {
  return useQuery<DomainAdminDasahboardGridConfig, Error>({
    queryKey: ['getDomainAdminGridConfig'],
    queryFn: () => getDomainAdminGridConfig(),
  });
};

export const useDomainAdminGridData = (queryParams) => {
  return useQuery<DomainAdminGridData, Error>({
    queryKey: ['getDomainAdminGridConfig', queryParams],
    queryFn: () => getDomainAdminGridData(queryParams),
    placeholderData: undefined,
  });
};

export const useChangeRoleToRMU = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: setRoleToRMU,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeactivateProject = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: deactivateProject,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useExportToExcel = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: exportToExcel,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveColumnDetails = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: saveColumnDetails,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDrawAssignmentFromPool = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    {
      successResult: number;
    },
    Error,
    number
  >({
    mutationFn: (payload: number) => drawAssignmentFromPool(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveTagClassificationDetails = (
  onSuccess?: (data: TaggingWidgetData) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<TaggingWidgetData, Error, ReviewTagCount>({
    mutationFn: (payload: ReviewTagCount) => getTaggingWidgetData(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useTagClassifications = () => {
  return useQuery<TagClassificationItem[], Error>({
    queryKey: ['useTagClassifications'],
    queryFn: () => getTagClassifications(),
    enabled: false,
  });
};

export const useRedactionTag = () => {
  return useQuery<RedactionTag[], Error>({
    queryKey: ['useRedactionTag'],
    queryFn: () => getRedactionTag(),
    enabled: false,
  });
};

export const useSaveReviewerProductivity = (
  onSuccess?: (res: ReviewerProductivityWidget) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ReviewerProductivityWidget,
    Error,
    ReviewProductivityRequest
  >({
    mutationFn: (payload: ReviewProductivityRequest) =>
      saveReviewerProductivityData(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({queryKey: ['getReviewProductivityData']});
      onSuccess?.(res);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveReviewProgress = (
  onSuccess?: (data: ReviewerProgressData) => void,
  onError?: (error: Error) => void
) => {
  return useMutation<
    ReviewerProgressData,
    Error,
    ReviewerProgressFilterDataRPW
  >({
    mutationFn: saveReviewProgress,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAllProjectsData = (
  loginUser: AuthenticatedUser,
  enabled: boolean
) => {
  return useQuery<IDashBoardHeaderResponse, Error>({
    queryKey: ['getAllProjectsData', loginUser],
    queryFn: () => getAllProjectsData(),
    enabled,
  });
};

export const useMenuData = (projectId: number) => {
  return useQuery<GetMenusType, Error>({
    queryKey: ['menu', projectId],
    queryFn: () => getMenusData(),
    enabled: true,
  });
};
